import type { ChannelHealth, ChannelHealthState, ContactThread } from '@/services/conversationService';
import { phoneDigits } from './conversationFormat';

/**
 * State of the WhatsApp number behind a contact (delivery contract 30.09.):
 * Meta can block a number (ban, restriction); from then on nothing reaches
 * the customers — neither the agent's replies nor staff messages. Pure: no
 * React, no I/O.
 */

/** Where the salon checks (and appeals) the block at Meta. */
export const META_ACCOUNT_QUALITY_URL = 'https://business.facebook.com/accountquality';

/** `whatsapp.channel_health` payload (persisted; replayed after a reconnect). */
export interface WhatsAppChannelHealthEvent {
  agent_number: string | null;
  state: ChannelHealthState;
  reason?: string | null;
  meta_status?: string | null;
  since?: string | null;
}

/** A missing channel is "unknown" and treated like ok: nothing is shown. */
export function isChannelBlocked(channel: ChannelHealth | null | undefined): boolean {
  return channel?.state === 'blocked';
}

/** Numbers as people write them (+49 611 …, 49611…) compared by their digits; empty never matches. */
export function sameAgentNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  const digits = phoneDigits(a);
  return digits.length > 0 && digits === phoneDigits(b);
}

function sameChannel(a: ChannelHealth | null | undefined, b: ChannelHealth): boolean {
  return (
    !!a &&
    a.state === b.state &&
    (a.reason ?? null) === (b.reason ?? null) &&
    (a.meta_status ?? null) === (b.meta_status ?? null) &&
    (a.since ?? null) === (b.since ?? null)
  );
}

/**
 * Apply `whatsapp.channel_health` to the open contact: only for the contact's
 * own agent number. `ok` clears the block (and with it the reason).
 */
export function applyChannelHealthEventToThread(thread: ContactThread, event: WhatsAppChannelHealthEvent): ContactThread {
  if (event.state !== 'ok' && event.state !== 'blocked') return thread;
  if (!sameAgentNumber(event.agent_number, thread.agent_number)) return thread;
  const channel: ChannelHealth =
    event.state === 'ok'
      ? { state: 'ok' }
      : {
          state: 'blocked',
          reason: event.reason ?? null,
          meta_status: event.meta_status ?? null,
          since: event.since ?? null,
        };
  return sameChannel(thread.channel, channel) ? thread : { ...thread, channel };
}

/**
 * The server refused a send because the channel is blocked: show it now,
 * even if the stream never told us. Reason and time come with the next read.
 */
export function markChannelBlocked(thread: ContactThread): ContactThread {
  return isChannelBlocked(thread.channel) ? thread : { ...thread, channel: { state: 'blocked' } };
}
